// ============================================================================
// CHARGEABLE COST WIDGET (DASHBOARD)
// ============================================================================
// Shows the Standard Cost of Chargeable timesheet entries logged during the
// selected period, grouped by Client or Engagement Type.
//
// THIS IS NOT THE ACCOUNTING WIP REGISTER. The cumulative cross-period WIP
// (`prima.tbl_engagement.total_wip_amount`) is left untouched and not read
// here. Per product decision, the widget is a period-activity report — not
// a snapshot of the WIP carrying value on the books. Budget / Invoiced /
// Outstanding bars are intentionally absent for the same reason: they are
// cumulative engagement-level amounts that don't fit a "this week" view.

import React, { useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EChart } from 'components/ui/EChart';
import { useWipSummaryWidget } from 'hooks/api/useDashboard';
import type { EChartsCoreOption } from 'echarts/core';

interface Props {
  managerId?: string;
  /** ISO date (YYYY-MM-DD) of the selected week start. */
  weekStartDate?: string;
  onDataClick?: (ctx: { groupLabel: string; value: number }) => void;
}

function formatCurrency(n: number): string {
  if (!n) return '₹ 0';
  return `₹ ${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function formatCompact(n: number): string {
  if (!n) return '₹ 0';
  const abs = Math.abs(n);
  if (abs >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (abs >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  if (abs >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n}`;
}

function truncateLabel(s: string, max: number = 28): string {
  if (!s) return '';
  return s.length <= max ? s : s.substring(0, max - 1) + '…';
}

/** Compute the week-end date (weekStart + 6 days) as YYYY-MM-DD. */
function computeWeekEnd(weekStartDate: string): string {
  const [y, m, d] = weekStartDate.split('-').map(Number);
  const start = new Date(y, m - 1, d);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
}

const WipSummaryWidget: React.FC<Props> = ({ managerId, weekStartDate, onDataClick }) => {
  const [groupBy, setGroupBy] = useState<'Client' | 'EngagementType'>('Client');

  const fromDate = weekStartDate || undefined;
  const toDate = weekStartDate ? computeWeekEnd(weekStartDate) : undefined;

  const { data, loading } = useWipSummaryWidget(managerId, groupBy, fromDate, toDate);

  const rawItems = data?.items || [];

  // Keep only groups with non-zero chargeable cost.
  const items = useMemo(
    () => rawItems.filter((i) => (i.wipAmount || 0) > 0),
    [rawItems],
  );

  const totalChargeable = data?.totalWip || 0;

  const hasRawItems = rawItems.length > 0;
  const hasMeaningfulData = items.length > 0;

  // Sort by amount desc so the biggest bar is at the top (yAxis is inverted).
  const sorted = useMemo(
    () => [...items].sort((a, b) => (b.wipAmount || 0) - (a.wipAmount || 0)),
    [items],
  );

  const chartData = useMemo(
    () => ({
      labels: sorted.map((i) => truncateLabel(i.groupLabel)),
      fullLabels: sorted.map((i) => i.groupLabel),
      values: sorted.map((i) => Number(i.wipAmount || 0)),
    }),
    [sorted],
  );

  const dynamicHeight = Math.max(220, sorted.length * 32 + 40);

  const option: EChartsCoreOption = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          if (!Array.isArray(params) || params.length === 0) return '';
          const idx = params[0].dataIndex;
          const full = chartData.fullLabels[idx] || '';
          const value = Number(params[0].value || 0);
          return `<div style="font-weight:600;margin-bottom:4px;">${full}</div>
                  <div>Chargeable Cost: <b>${formatCurrency(value)}</b></div>`;
        },
      },
      grid: { top: 10, bottom: 16, left: 10, right: 20, containLabel: true },
      xAxis: {
        type: 'value',
        axisLabel: { formatter: (v: number) => formatCompact(v), fontSize: 10 },
      },
      yAxis: {
        type: 'category',
        data: chartData.labels,
        inverse: true,
        axisLabel: { fontSize: 10, width: 140, overflow: 'truncate' },
        axisTick: { alignWithLabel: true },
      },
      series: [
        {
          name: 'Chargeable Cost',
          type: 'bar',
          data: chartData.values,
          itemStyle: { color: '#16a34a' },
          barWidth: 12,
        },
      ],
    }),
    [chartData],
  );

  const events = useMemo(
    () => ({
      click: (params: any) => {
        if (onDataClick && typeof params?.dataIndex === 'number') {
          const fullLabel = chartData.fullLabels[params.dataIndex] || params.name || '';
          onDataClick({ groupLabel: fullLabel, value: Number(params.value || 0) });
        }
      },
    }),
    [onDataClick, chartData.fullLabels],
  );

  // Tooltip describing what is and isn't included. Keeps the user honest about
  // the difference between this period-activity widget and the cumulative WIP
  // register on the engagement record.
  const tooltipText = [
    'Includes: timesheet entries with charge type = Chargeable',
    '  AND status Draft or Submitted (deleted entries are excluded)',
    'Period: the week chosen in the dashboard period selector',
    '  Standard Cost = duration_hours × standard_rate at time of entry',
    'Scope: employees reporting to the selected manager (direct reportees).',
    '  Anchored on the EMPLOYEE\'S reporting line, NOT engagement ownership —',
    '  hours your team logs on another manager\'s engagement are included here.',
    '  Sub-managers\' teams and the manager\'s own entries are NOT included.',
    'Engagements at any status (Open / Closed / Completed) are included.',
    'Top 10 groups by chargeable cost.',
    '',
    'NOTE: this is period activity, not the cumulative WIP register on',
    'the engagement record. The locked-in total_wip_amount is NOT used.',
  ].join('\n');

  const renderBody = () => {
    if (loading && !hasRawItems) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
          Loading...
        </div>
      );
    }
    if (!hasMeaningfulData) {
      return (
        <div
          style={{
            padding: '2rem 1rem',
            textAlign: 'center',
            color: 'var(--text-muted)',
            fontSize: '0.8rem',
            lineHeight: 1.5,
          }}
        >
          <strong>No chargeable activity this period.</strong>
          <br />
          No chargeable timesheet entries fall within the selected week for the current scope.
        </div>
      );
    }
    return (
      <div style={{ maxHeight: 360, overflowY: 'auto' }}>
        <EChart option={option} height={dynamicHeight} loading={loading} notMerge onEvents={events} />
      </div>
    );
  };

  return (
    <div className="dashboard-widget">
      <div className="dashboard-widget-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <IconSystem.dollarSign size={18} />
          <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600 }}>Chargeable Cost (Period)</h3>
          <span
            title={tooltipText}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              cursor: 'help',
              color: 'var(--text-muted)',
            }}
            aria-label="What's included in this widget"
          >
            <IconSystem.info size={14} />
          </span>
        </div>
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          <button
            className={`btn ${groupBy === 'Client' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem' }}
            onClick={() => setGroupBy('Client')}
          >
            By Client
          </button>
          <button
            className={`btn ${groupBy === 'EngagementType' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem' }}
            onClick={() => setGroupBy('EngagementType')}
          >
            By Type
          </button>
        </div>
      </div>
      {renderBody()}
      {hasRawItems && (
        <div
          style={{
            borderTop: '1px solid var(--border-color)',
            paddingTop: '0.5rem',
            marginTop: '0.25rem',
            fontSize: '0.75rem',
            display: 'flex',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: '0.5rem',
          }}
        >
          <span>Total: <strong>{formatCurrency(totalChargeable)}</strong></span>
        </div>
      )}
    </div>
  );
};

export default WipSummaryWidget;
